import React, { Children, Fragment } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
interface Slide4Props {
  isDark?: boolean;
}
export function Slide4({
  isDark = false
}: Slide4Props) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-861">
      <div className="min-h-full flex flex-col justify-start md:justify-center max-w-5xl mx-auto px-6 sm:px-12 py-20 sm:py-24" data-id="element-862">
        <motion.div variants={itemVariants} className="mb-12 sm:mb-16 text-center" data-id="element-863">
          <h1 className={`text-3xl sm:text-5xl font-serif font-medium mb-5 sm:mb-6 tracking-tight ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-864">
            Why Heliona Is a Smart Investment
          </h1>
          <p className={`text-base sm:text-xl max-w-2xl mx-auto px-4 ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-865">
            Replace fragmented tools with one integrated workspace
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-start mb-10 sm:mb-12" data-id="element-866">
          {/* Fragmented Costs */}
          <motion.div variants={itemVariants} className={`rounded-3xl p-7 sm:p-10 shadow-lg ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-gradient-to-br from-heliona-slate/10 to-heliona-slate/5 border border-heliona-slate/30'}`} data-id="element-867">
            <h3 className={`text-lg sm:text-2xl font-bold mb-6 sm:mb-8 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-868">
              Typical Fragmented Spend
            </h3>

            <div className="space-y-4 sm:space-y-5 mb-6 sm:mb-8" data-id="element-869">
              {[{
              label: 'Coaching Platform',
              brand: 'BetterUp / EZRA',
              cost: '$3,000–5,000'
            }, {
              label: 'EAP / Clinical',
              brand: 'Lyra / Spring',
              cost: '$1,200–2,400'
            }, {
              label: 'Engagement Surveys',
              brand: 'Culture Amp / Korn Ferry',
              cost: '$600–1,200'
            }, {
              label: 'L&D Content',
              brand: 'Masterclass / Headspace',
              cost: '$500–1,200'
            }, {
              label: 'Admin Overhead & Integration',
              brand: 'Internal ops',
              cost: '$1,000–2,000'
            }].map((item, idx) => <div key={idx} className={`flex justify-between items-center pb-3 sm:pb-4 border-b ${isDark ? 'border-white/10' : 'border-heliona-slate/20'}`} data-id="element-870">
                  <div data-id="element-871">
                    <span className={`text-sm sm:text-lg font-medium block ${isDark ? 'text-white/80' : 'text-heliona-charcoal'}`} data-id="element-872">
                      {item.label}
                    </span>
                    <span className={`text-xs sm:text-sm ${isDark ? 'text-white/50' : 'text-heliona-text-gray/70'}`} data-id="element-873">
                      {item.brand}
                    </span>
                  </div>
                  <span className={`font-mono text-base sm:text-xl font-semibold flex-shrink-0 ml-4 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-874">
                    {item.cost}
                  </span>
                </div>)}
            </div>

            <div className={`pt-5 sm:pt-6 border-t-2 ${isDark ? 'border-white/20' : 'border-heliona-slate/30'}`} data-id="element-875">
              <div className="flex justify-between items-end" data-id="element-876">
                <span className={`font-semibold text-base sm:text-xl ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-877">
                  Fragmented Total
                </span>
                <span className="text-2xl sm:text-4xl font-bold text-red-500" data-id="element-878">
                  $6,300–11,800/yr
                </span>
              </div>
            </div>
          </motion.div>

          {/* Heliona Value */}
          <motion.div variants={itemVariants} className="space-y-6 sm:space-y-8" data-id="element-879">
            <h3 className={`text-lg sm:text-2xl font-bold mb-6 sm:mb-8 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-880">
              What Heliona Delivers
            </h3>

            <ul className="space-y-4 sm:space-y-5" data-id="element-881">
              {['Continuous leadership insight', 'Targeted coaching and care', 'Personalized development', 'Executive visibility'].map((item, i) => <li key={i} className="flex items-center gap-3 sm:gap-4" data-id="element-882">
                  <div className="bg-heliona-mint/20 p-2 sm:p-2.5 rounded-xl flex-shrink-0 border border-heliona-mint/40" data-id="element-883">
                    <Check className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-884" />
                  </div>
                  <span className={`text-base sm:text-xl ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-885">
                    {item}
                  </span>
                </li>)}
            </ul>

            <div className={`rounded-2xl p-6 sm:p-8 border border-heliona-mint/30 mt-8 sm:mt-10 mb-16 sm:mb-0 shadow-sm ${isDark ? 'bg-heliona-mint/10' : 'bg-gradient-to-br from-heliona-mint/10 to-heliona-mint/5'}`} data-id="element-886">
              <p className={`text-base sm:text-xl font-medium leading-relaxed ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-887">
                Pays for itself by recovering a small fraction of lost
                leadership capacity
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>;
}